// Google Drive auth wrapper, isolated from the heavy `src/services` barrel.
// Importing this file pulls only `GoogleEdgeFnClient` + `supabaseClient` +
// the OAuth helper — not the full Refine/Email/Invoice/Bank service chain.
//
// The barrel `src/services/index.ts` re-exports these for back-compat.

import { GoogleEdgeFnClient } from "@shared/services/edgeFn/GoogleEdgeFnClient";
import { supabaseClient } from "../../utility/supabaseClient";
import { initiateGoogleOAuth } from "./googleOauth";
import { errorText } from "@shared/errorText";

const REAUTH_COOLDOWN_MS = 30_000;
let lastReauthAt = 0;

function triggerReauth(missingScopes?: string[]): void {
  const now = Date.now();
  if (now - lastReauthAt < REAUTH_COOLDOWN_MS) return;
  lastReauthAt = now;
  initiateGoogleOAuth(missingScopes?.length ? missingScopes : undefined);
}

interface ReauthEnvelope {
  requiresReauth?: boolean;
  error?: string;
  missingScopes?: string[];
}

// supabase-js rejects every non-2xx invoke with the fixed message "Edge
// Function returned a non-2xx status code"; the requiresReauth envelope
// google-api-service returns (TOKEN_MISSING / MISSING_SCOPES /
// INVALID_REFRESH_TOKEN) is only reachable via `error.context` (a Response).
async function readReauthEnvelope(error: unknown): Promise<ReauthEnvelope | null> {
  const ctx = (error as { context?: { json?: () => Promise<unknown> } } | null)?.context;
  if (!ctx || typeof ctx.json !== "function") return null;
  try {
    const body = (await ctx.json()) as ReauthEnvelope | null;
    return body?.requiresReauth ? body : null;
  } catch {
    return null;
  }
}

export const googleClient = new GoogleEdgeFnClient(supabaseClient);

export async function withGoogleAuth<T>(fn: (client: GoogleEdgeFnClient) => Promise<T>): Promise<T> {
  try {
    return await fn(googleClient);
  } catch (error: unknown) {
    // BaseEdgeFnClient surfaces the envelope's fields on the thrown error —
    // the message is already the server's ("Please re-authenticate.").
    const fields = error as ReauthEnvelope | null;
    if (fields?.requiresReauth) {
      triggerReauth(fields.missingScopes);
      throw error;
    }
    const reauth = await readReauthEnvelope(error);
    if (reauth) {
      triggerReauth(reauth.missingScopes);
      throw new Error(reauth.error ?? errorText(error) ?? String(error));
    }
    const msg = errorText(error) ?? String(error);
    if (msg.includes("authentication") || msg.includes("401") || msg.includes("reauth")) triggerReauth();
    throw error;
  }
}

// Legacy — for callers not yet migrated to typed client methods.
export async function callGoogleMethod<T = unknown>(
  method: string,
  body: Record<string, unknown> = {},
): Promise<T> {
  return withGoogleAuth(() => googleClient.callOrThrow<T>("google-api-service", { method, body }));
}
